
import React from "react";

const StakeholderVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
      This lesson breaks the belief that your boss alone controls your growth. It introduces stakeholders as the wider web of people whose decisions, opinions, and casual narratives shape promotions, projects, and opportunities. It maps the main types, decision makers, influencers, gatekeepers, amplifiers, and the people affected by your work. The core idea is simple: if only your boss knows your impact, you stay invisible when group decisions happen.
      </p>
    </div>
  );

  const transcriptHtml = `
    <p>Remember one of the biggest myths about career growth? That your boss decides everything.</p>

    <p>As long as you keep them happy, smile in one-on-ones, and drop just the right amount of "Got it!" in your emails, you're set for the next level.</p>

    <p>But in reality, promotions, opportunities, and even key projects are shaped by a <strong>whole web of people</strong> you probably don't even think about.</p>

    <p>These people are your <strong>stakeholders</strong>.</p>

    <p>A stakeholder is anyone whose opinion, decision, or even narrative about you (even a Slack message) can impact your growth. And it's not just about hierarchy but more about <strong>influence</strong>.</p>

    <p>Think of it this way:</p>

    <p>You're a mid-level professional, say, an Assistant Manager in Accounts.</p>
    <p>You handle reports, coordinate across departments, close numbers, and keep operations running smoothly.</p>
    <p>But your performance doesn't exist in isolation.</p>

    <p>Every week, you're interacting with an invisible web of people, each with the power to shape how you grow.</p>

    <p><strong>There are four key types of stakeholders.</strong></p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">At the top are your Decision-Makers</h3>
    <p>your manager, your boss's boss, your skip-level leader, the promotion committee and more. They sign off, recommend, and decide whose work deserves to move up.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Next come the Influencers</h3>
    <p>the senior Individual Contributors, project sponsors, chiefs of staff, or architects.</p>
    <p>They're not your direct bosses, but their opinions carry quiet weight. When they vouch for your judgment, others listen.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Then, the Gatekeepers</h3>
    <p>finance, legal, HR, PMO.</p>
    <p>They control processes, budgets, and timelines.</p>
    <p>If they see you as dependable, doors open faster.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">You also have the Amplifiers</h3>
    <p>your trusted peers, cross-functional partners, and customer-facing teams. They're the ones who casually mention your name in other rooms:</p>
    <p><em>"Oh, she handled that escalation beautifully."</em></p>
    <p>That's how reputations travel.</p>

    <p>And finally, the Beneficiaries and Critics — the people who feel your work: sales teams, clients, support staff, end-users.</p>
    <p>If their experience improves because of you, their feedback becomes a powerful echo.</p>

    <p><strong>If they can green-light, block, fund, or talk about your work, they're a stakeholder.</strong></p>

    <p>The mistake most people make? Thinking only their boss matters. But promotions rarely hinge on one voice, they're like group projects.</p>

    <p>Calibrations pull feedback from all these people. So, if your boss is the only one who knows your work, you're one weak link away from being invisible in the room where decisions are made.</p>

    <p><strong>Have you identified your stakeholders?</strong></p>
  `;

  const transcript = (
    <div
      className="pt-2 pb-6 text-body leading-7 text-[#484848] max-w-2xl"
      dangerouslySetInnerHTML={{ __html: transcriptHtml }}
    />
  );

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  // ✅ CORRECT: Return plain object (no JSX wrapper)
  return { 
    about, 
    transcript, 
    // relatedLinks,
    // Include the question so VideoContainer can show it
    question: "What's your understanding of stakeholder visibility?"
  };
};

export default StakeholderVideo;